/**
 * O que faz: Exibe um card individual de métrica (KPI) com badge de tendência, detalhes e ação de clique opcional.
 * Onde usa: Grid de métricas do dashboard.
 * Conexões: Ícones do lucide-react e props vindas de DashboardMetrics.
 * Tabelas: Não acessa tabelas diretamente; apenas apresenta dados já processados.
 * Data: 11/06/2026
 */

import { LucideIcon, ArrowUpRight, TrendingUp, TrendingDown } from 'lucide-react';

interface MetricCardProps {
  title: string;
  value: string;
  icon: LucideIcon;
  change?: string;
  changeType?: 'positive' | 'negative';
  onClick?: () => void;
  clickable?: boolean;
  details?: Array<{ label: string; value: string }>;
  isUpdating?: boolean;
  isLoading?: boolean;
}

export const MetricCard = ({
  title,
  value,
  icon: Icon,
  change,
  changeType,
  onClick,
  clickable = false,
  details,
  isUpdating = false,
  isLoading = false,
}: MetricCardProps) => {
  const isInteractive = clickable && !!onClick;

  return (
    <div
      className={`group relative flex h-full flex-col bg-card rounded-2xl border border-border p-4 shadow-[0_1px_2px_rgba(19,41,27,0.05),0_3px_10px_rgba(19,41,27,0.05)] ${
        isInteractive
          ? 'hover:border-[#c4cdbb] hover:shadow-[0_1px_2px_rgba(19,41,27,0.05),0_8px_20px_-4px_rgba(19,41,27,0.08),0_22px_44px_-18px_rgba(19,41,27,0.14)]'
          : 'hover:shadow-sm'
      }`}
      aria-busy={isLoading || isUpdating}
    >
      {/* Label + ícone */}
      <div className="flex items-start justify-between gap-2">
        <span className="text-[13px] font-medium text-muted-foreground leading-snug">
          {title}
        </span>
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent transition-colors duration-200 group-hover:bg-[#e6ffa0]">
          <Icon className="h-4 w-4 text-accent-foreground" aria-hidden="true" />
        </div>
      </div>

      {/* Valor + tendência */}
      <div className="mt-2 flex flex-wrap items-end justify-between gap-x-2 gap-y-1">
        <p
          className={`lg-display text-xl sm:text-[1.75rem] font-bold tracking-tight tabular-nums text-foreground leading-none transition-opacity duration-300 ${
            isUpdating ? 'opacity-50' : 'opacity-100'
          }`}
        >
          {isLoading ? (
            <span className="inline-flex h-7 w-20 animate-pulse rounded-md bg-muted sm:h-8" aria-hidden="true" />
          ) : value}
          {isLoading && <span className="sr-only">Carregando {title}</span>}
        </p>

        {change && (
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums ${
              changeType === 'negative'
                ? 'bg-danger-subtle text-danger-text'
                : 'bg-success-subtle text-success-text'
            }`}
          >
            {changeType === 'negative' ? (
              <TrendingDown className="h-3 w-3" aria-hidden="true" />
            ) : (
              <TrendingUp className="h-3 w-3" aria-hidden="true" />
            )}
            {change}
          </span>
        )}
      </div>

      {/* Espaçador flexível: empurra detalhes/ação para a base do card,
          distribuindo a altura extra entre o valor e a base em vez de vazio inferior */}
      <div className="flex-1" aria-hidden="true" />

      {/* Sub-métricas (ex.: enviadas/recebidas) */}
      {details && details.length > 0 && !isLoading && (
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border pt-2">
          {details.map((detail) => (
            <div key={detail.label} className="flex items-baseline gap-1.5 min-w-0">
              <span className="text-sm font-semibold tabular-nums text-foreground">{detail.value}</span>
              <span className="truncate text-[11px] text-muted-foreground">{detail.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Ação explícita evita card inteiro como botão com controles filhos aninhados */}
      {isInteractive && (
        <button
          type="button"
          onClick={onClick}
          aria-label={`${title}: ${value}. Ver detalhes`}
          className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-accent-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          Ver detalhes
          <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      )}

      {/* Pulso discreto durante atualização suave */}
      {isUpdating && (
        <span className="absolute left-3 bottom-3 h-1.5 w-1.5 rounded-full bg-primary animate-ping" aria-hidden="true" />
      )}
    </div>
  );
};
